import { Controller } from '@hotwired/stimulus'

// Manages the conversion note modal in create + edit modes.
// Edit mode is triggered by the 'conversion-note:edit' window event
// dispatched by report_chart_controller when an annotation dot is clicked.
export default class extends Controller {
  static targets = [
    'form',
    'methodField',
    'deployedAtInput',
    'titleInput',
    'bodyInput',
    'deleteLink',
    'modalTitle',
    'picker',
    'pickerSelect',
    'errors',
    'submitButton'
  ]

  static values = { baseUrl: String }

  connect() {
    this.annotations = []
    this.editingId = null
    this.defaultFormAction = this.formTarget.action
    this.defaultDate = this.deployedAtInputTarget.value
    this.boundReset = this.reset.bind(this)
    this.element.addEventListener('hidden.bs.modal', this.boundReset)
  }

  disconnect() {
    this.element.removeEventListener('hidden.bs.modal', this.boundReset)
  }

  openEdit(event) {
    this.annotations = event.detail.annotations || []
    if (this.annotations.length === 0) return

    this.buildPicker()
    this.select(0)
    window.bootstrap.Modal.getOrCreateInstance(this.element).show()
  }

  pick(event) {
    this.select(parseInt(event.currentTarget.value, 10))
  }

  select(index) {
    const annotation = this.annotations[index]
    if (!annotation) return

    const url = `${this.baseUrlValue}/${annotation.id}`
    this.editingId = annotation.id
    this.formTarget.action = url
    this.methodFieldTarget.disabled = false
    this.methodFieldTarget.value = 'patch'
    this.setDate(annotation.deployed_at)
    this.titleInputTarget.value = annotation.title || ''
    this.bodyInputTarget.value = annotation.body || ''
    this.deleteLinkTarget.classList.remove('d-none')
    this.modalTitleTarget.textContent = 'Edit Conversion Note'
    this.pickerSelectTarget.value = String(index)
  }

  reset() {
    this.annotations = []
    this.editingId = null
    this.formTarget.action = this.defaultFormAction
    this.methodFieldTarget.disabled = true
    this.setDate(this.defaultDate)
    this.titleInputTarget.value = ''
    this.bodyInputTarget.value = ''
    this.deleteLinkTarget.classList.add('d-none')
    this.modalTitleTarget.textContent = 'Conversion Note'
    this.pickerTarget.classList.add('d-none')
    this.pickerSelectTarget.replaceChildren()
    this.hideErrors()
  }

  buildPicker() {
    this.pickerSelectTarget.replaceChildren()
    if (this.annotations.length < 2) {
      this.pickerTarget.classList.add('d-none')
      return
    }

    this.annotations.forEach((annotation, index) => {
      const option = document.createElement('option')
      option.value = index
      option.textContent = annotation.date
        ? `${annotation.title} — ${annotation.date}`
        : annotation.title
      this.pickerSelectTarget.appendChild(option)
    })
    this.pickerTarget.classList.remove('d-none')
  }

  setDate(dateString) {
    const input = this.deployedAtInputTarget
    if (input._flatpickr) {
      input._flatpickr.setDate(dateString, false)
    } else {
      input.value = dateString
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content
  }

  async submit(event) {
    event.preventDefault()
    this.hideErrors()
    this.submitButtonTarget.disabled = true

    try {
      const form = this.formTarget
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: {
          'X-CSRF-Token': this.csrfToken(),
          Accept: 'application/json'
        }
      })

      if (response.ok) {
        const comment = await response.json()
        window.dispatchEvent(
          new CustomEvent('conversion-note:saved', { detail: { comment } })
        )
        window.bootstrap.Modal.getOrCreateInstance(this.element).hide()
      } else if (response.status === 422) {
        const data = await response.json()
        this.showErrors(data.errors || [])
      } else {
        this.showErrors(['Unable to save note. Please try again.'])
      }
    } catch {
      this.showErrors(['Unable to save note. Please try again.'])
    } finally {
      this.submitButtonTarget.disabled = false
    }
  }

  async destroy() {
    if (!this.editingId) return
    if (!window.confirm('Delete this note? This cannot be undone.')) return

    const url = `${this.baseUrlValue}/${this.editingId}`
    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.csrfToken(),
          Accept: 'application/json'
        }
      })

      if (response.ok) {
        window.dispatchEvent(
          new CustomEvent('conversion-note:deleted', {
            detail: { id: this.editingId }
          })
        )
        window.bootstrap.Modal.getOrCreateInstance(this.element).hide()
      } else {
        this.showErrors(['Unable to delete note. Please try again.'])
      }
    } catch {
      this.showErrors(['Unable to delete note. Please try again.'])
    }
  }

  showErrors(errors) {
    this.errorsTarget.textContent = errors.join(', ')
    this.errorsTarget.classList.remove('d-none')
  }

  hideErrors() {
    this.errorsTarget.textContent = ''
    this.errorsTarget.classList.add('d-none')
  }
}
